import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Tabs, Tab } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TopPage from 'components/Shared/TopPage';
import { CustomTypography } from 'components/Shared/TopPage/styles';
import Loader from 'components/Shared/Loader';
import { useSnackbar } from 'notistack';
import api from 'services/api';
import UsersTab from './UsersTab';
import ClientsTab from './ClientsTab';
import ProfilesTab from './ProfilesTab';
import { CustomCardContent } from '../style';

interface AccessGroupData {
  id: number;
  name: string;
  display_name: string;
  active: boolean;
}

function ManageAccessGroup() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [accessGroup, setAccessGroup] = useState<AccessGroupData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!id) return;

    const loadAccessGroupData = async () => {
      try {
        setIsLoading(true);
        const response = await api.get<{ data: AccessGroupData }>(`/access_groups/${id}`);
        setAccessGroup(response.data);
      } catch (error) {
        enqueueSnackbar('Erro ao carregar grupo de acesso', { variant: 'error' });
        navigate('/grupos-acesso');
      } finally {
        setIsLoading(false);
      }
    };

    loadAccessGroupData();
  }, [id, navigate, enqueueSnackbar]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleBackClick = () => {
    navigate('/grupos-acesso');
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  if (isLoading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='60vh'>
        <Loader />
      </Box>
    );
  }

  if (!accessGroup) {
    return (
      <Box>
        <TopPage title='Gerenciar Grupo de Acesso' />
        <Box>Grupo de acesso não encontrado</Box>
      </Box>
    );
  }

  return (
    <>
      <Box display='flex' alignItems='center'>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick} variant='text' style={{ marginBottom: '16px' }}>
          Voltar
        </Button>
        <CustomTypography variant='h1' sx={{ mb: 0 }}>{`Grupo: ${accessGroup.name}`}</CustomTypography>
      </Box>

      <Card style={CustomCardContent}>
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label='Usuários' />
              <Tab label='Clientes (CNPJs)' />
              <Tab label='Perfis' />
            </Tabs>
          </Box>

          <Box sx={{ pt: 3 }}>
            {tabValue === 0 && id && (
              <UsersTab
                key={`users-${refreshKey}`}
                accessGroupId={parseInt(id, 10)}
                accessGroupName={accessGroup.name}
                onRefresh={handleRefresh}
              />
            )}
            {tabValue === 1 && id && (
              <ClientsTab key={`clients-${refreshKey}`} accessGroupId={parseInt(id, 10)} onRefresh={handleRefresh} />
            )}
            {tabValue === 2 && id && (
              <ProfilesTab key={`profiles-${refreshKey}`} accessGroupId={parseInt(id, 10)} onRefresh={handleRefresh} />
            )}
          </Box>
        </CardContent>
      </Card>
    </>
  );
}

export default ManageAccessGroup;
